import { useQuery } from '@tanstack/react-query'
import { FC, useMemo } from 'react'
import { useTimeout } from 'react-use'
import { Box, Description, Dots, Status, VStack } from '@revolut-internal/ui-kit'

import { getMessageColorSchema } from 'components'
import { QueryKey } from 'lib/queryClient'
import {
  apiClient,
  AuthorRole,
  GetFixedPromptTextGenerationOutState,
} from 'lib/apiClient'
import { CollapsibleChatMessage } from 'pages/AiProcessDebugV2/components/CollapsibleChatMessage'
import { MessageDate, TextMessage } from 'components/ChatMessage/components'
import { useProcessDebugDetailsContext } from 'pages/AiProcessDebugV2/hooks/useProcessDebugDetailsContext'

import { INITIAL_DELAY } from './FixedPromptTextGeneration.constants'
import { getPromptRefetchInterval } from './FixedPromptTextGeneration.utils'

interface FixedPromptTextGenerationProps {
  createdAt: string
}

export const FixedPromptTextGeneration: FC<FixedPromptTextGenerationProps> = ({
  createdAt,
}) => {
  const {
    currentFixedPromptTextGenerationIndex,
    currentFixedPromptTextGeneration,
    fixedPromptTextGenerationsCount,
    onPrevClick,
    onNextClick,
  } = useProcessDebugDetailsContext()

  const id = currentFixedPromptTextGeneration?.id

  const [isReady] = useTimeout(INITIAL_DELAY)

  const { data } = useQuery({
    enabled: Boolean(id && isReady()),
    queryKey: [QueryKey.getFixedPromptTextGeneration, id],
    queryFn: () => apiClient.getFixedPromptTextGeneration(id!),
    refetchInterval: getPromptRefetchInterval,
  })

  // TODO: Create a new component for the polling message (https://revolut.atlassian.net/browse/AIR-177)
  const element = useMemo(() => {
    switch (data?.state) {
      case GetFixedPromptTextGenerationOutState.Failed:
        return (
          <VStack space="s-8">
            <Status useIcon="ExclamationMarkOutline" color="red">
              Failed
            </Status>
            <Description>
              {data.result.code} - {data.result.message}
            </Description>
          </VStack>
        )

      case GetFixedPromptTextGenerationOutState.Generated: {
        const colorSchema = getMessageColorSchema({
          messageType: AuthorRole.Agent,
          isDebugView: true,
        })

        return (
          <Box display="inline-table">
            <TextMessage text={data.result.response.text} colorSchema={colorSchema} />
            <MessageDate createdAt={createdAt} colorSchema={colorSchema} />
          </Box>
        )
      }
      default:
        return <Dots aria-label="Loading" color="grey-tone-50" />
    }
  }, [data, createdAt])

  if (!id) {
    return null
  }

  return (
    <CollapsibleChatMessage
      label="Updated"
      collapsible
      currentPage={currentFixedPromptTextGenerationIndex}
      totalPages={fixedPromptTextGenerationsCount}
      onPrevClick={onPrevClick}
      onNextClick={onNextClick}
    >
      {element}
    </CollapsibleChatMessage>
  )
}
